import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Pagination,
  IconButton,
  MenuItem,
  Menu,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useDispatch } from "react-redux";
import {
  getAllUsersData,
  blockUser,
  unblockUser,
  getBlockedUsers,
  getSubscriptionHistory,
} from "store/actions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockIcon from "@mui/icons-material/Block";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import StatusChip from "../ui-component/StatusChip";

const CommunityUsersManager = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const [allUsers, setAllUsers] = useState([]);
  const [communityUsers, setCommunityUsers] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [menuAnchorEls, setMenuAnchorEls] = useState({});
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userSubscriptionHistory, setUserSubscriptionHistory] = useState([]);

  // ✅ Debounced API call whenever search, tab, or page changes
  useEffect(() => {
    const timer = setTimeout(() => {
      loadData();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, activeTab, page]);

  // ✅ Fetch users + posts depending on tab and search
  const loadData = () => {
    setLoading(true);

    dispatch(
      getAllUsersData(page + 1, searchTerm, (result) => {
        const users = result.data || [];
        const backendTotal = result.totalPages || 1;

        if (activeTab === "all") {
          setUniqueUsers(users);
          setTotalPages(backendTotal);
          setLoading(false);
        }

        if (activeTab === "blocked") {
          dispatch(
            getBlockedUsers(page + 1, 10, searchTerm, (blockedRes) => {
              setUniqueUsers(blockedRes.data || []);
              setTotalPages(blockedRes.totalPages || 1);
              setLoading(false);
            })
          );
        }

      })
    );
  };


  const handleChangePage = (event, value) => {
    setPage(value - 1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleMenuClick = (event, id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id) => {
    setMenuAnchorEls((prev) => ({ ...prev, [id]: null }));
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setOpenDetailDialog(true);

    // Fetch subscription history
    dispatch(
      getSubscriptionHistory(user._id, (history) => {
        setUserSubscriptionHistory(history || []);
      })
    );

    handleMenuClose(user._id);
  };
  const handleBlockUser = (userId) => {
    setActionInProgress(true);
    dispatch(
      blockUser(userId, () => {
        setActionInProgress(false);
        setOpenDetailDialog(false);
        loadData();
      })
    );
    handleMenuClose(userId);
  };

  const handleUnblockUser = (userId) => {
    setActionInProgress(true);
    dispatch(
      unblockUser(userId, () => {
        setActionInProgress(false);
        setOpenDetailDialog(false);
        loadData();
      })
    );
    handleMenuClose(userId);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const downloadCSV = (users) => {
    const csvRows = [];

    // Header row
    csvRows.push([
      "Name",
      "Email",
      "Phone",
      "Status",
      "Joined"
    ].join(","));

    users.forEach((user) => {
      csvRows.push([
        `"${user?.name || ""}"`,
        `"${user?.email || ""}"`,
        `"${user?.mobileNumber || ""}"`,
        user?.isBlocked ? "Blocked" : "Active",
        formatDate(user?.createdAt)
      ].join(","));
    });

    const csvData = csvRows.join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "users_list.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    setLoading(true);
    if (activeTab === "all") {
      dispatch(
        getAllUsersData(1, searchTerm, (result) => {
          setLoading(false);
          const users = result.data || [];
          downloadCSV(users);
        }, 0, false)
      );
    } else if (activeTab === "blocked") {
      dispatch(
        getBlockedUsers(1, 0, searchTerm, (blockedRes) => {
          setLoading(false);
          const users = blockedRes.data || [];
          downloadCSV(users);
        }, false)
      );
    }
  };

  return (
    <>
      <Grid container spacing={2} className="content-management">
        <Grid item xs={12}>
          {/* Header Section */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              {/* Title */}
              <Grid item xs={12} md={3}>
                <Typography
                  variant="h5"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    fontWeight: 600,
                  }}
                >
                  <PeopleIcon color="primary" />
                  Manage Users
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, ml: 4.5 }}
                >
                  View, block, and manage your user base
                </Typography>
              </Grid>

              {/* Filter Buttons */}
              <Grid
                item
                xs={12}
                md={5}
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: "center", // ⬅ Centers everything
                }}
              >
                <Button
                  variant={activeTab === "all" ? "contained" : "outlined"}
                  color="primary"
                  size="small"
                  sx={{ borderRadius: 2, px: 2, textTransform: "none", fontWeight: 500 }}
                  onClick={() => {
                    setActiveTab("all");
                    setPage(0);
                  }}
                >
                  All Users
                </Button>

                <Button
                  variant={activeTab === "blocked" ? "contained" : "outlined"}
                  color="error"
                  size="small"
                  sx={{ borderRadius: 2, px: 2, textTransform: "none", fontWeight: 500 }}
                  onClick={() => {
                    setActiveTab("blocked");
                    setPage(0);
                  }}
                >
                  Blocked Users
                </Button>

                {/* ⭐ ICON-ONLY DOWNLOAD BUTTON ⭐ */}
                <IconButton
                  color="secondary"
                  sx={{
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    width: 36,
                    height: 36,
                  }}
                  onClick={handleDownload}
                >
                  <DownloadIcon />
                </IconButton>
              </Grid>


              {/* Search Field */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Search Users"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by name or email"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Paper>


          <TableContainer sx={{ mt: 3 }}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography textAlign="center" sx={{ py: 4 }}>
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : uniqueUsers.length > 0 ? (
                  uniqueUsers.map((user) => {
                    return (
                      <TableRow
                        key={user._id}
                        sx={{
                          "&:hover": { backgroundColor: "#f5f5f5" },
                          backgroundColor: user.isBlocked ? "rgba(244, 67, 54, 0.05)" : "transparent",
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar
                              src={user?.profileImage?.signedUrl}
                              alt={user?.name}
                              sx={{ width: 40, height: 40 }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {user?.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{user?.email}</TableCell>
                        <TableCell>{user?.mobileNumber || "-"}</TableCell>
                        <TableCell>{formatDate(user?.createdAt)}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.isBlocked ? "Blocked" : "Active"}
                            color={user.isBlocked ? "error" : "success"}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={(e) => handleMenuClick(e, user._id)}>
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <Menu
                            anchorEl={menuAnchorEls[user._id]}
                            open={Boolean(menuAnchorEls[user._id])}
                            onClose={() => handleMenuClose(user._id)}
                          >
                            <MenuItem onClick={() => handleView(user)}>
                              <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
                              View Details
                            </MenuItem>
                            {!user.isBlocked ? (
                              <MenuItem
                                onClick={() => handleBlockUser(user._id)}
                                sx={{ color: "error.main" }}
                              >
                                <BlockIcon sx={{ mr: 1, fontSize: 18 }} />
                                Block User
                              </MenuItem>
                            ) : (
                              <MenuItem
                                onClick={() => handleUnblockUser(user._id)}
                                sx={{ color: "success.main" }}
                              >
                                <PersonAddIcon sx={{ mr: 1, fontSize: 18 }} />
                                Unblock User
                              </MenuItem>
                            )}
                          </Menu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <PersonRemoveIcon sx={{ fontSize: 48, color: "textSecondary", mb: 2 }} />
                        <Typography variant="body2" color="textSecondary">
                          No users found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid container justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            color="secondary"
            size="small"
            page={page + 1}
            onChange={handleChangePage}
          />
        </Grid>
      </Grid>

      {/* Dialog for user details */}
      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>User Details</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedUser && (
            <Box>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Avatar
                  src={selectedUser?.profileImage?.signedUrl}
                  alt={selectedUser?.name}
                  sx={{ width: 80, height: 80, mx: "auto", mb: 2 }}
                />
                <Typography variant="h6">{selectedUser?.name}</Typography>
                <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={selectedUser.isBlocked ? "Blocked" : "Active"}
                    color={selectedUser.isBlocked ? "error" : "success"}
                    size="small"
                  />
                </Box>
              </Box>

              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2"><strong>Email:</strong></Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>{selectedUser?.email}</Typography>

                <Typography variant="subtitle2"><strong>Phone:</strong></Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>{selectedUser?.mobileNumber || "-"}</Typography>

                <Typography variant="subtitle2"><strong>Date of Birth:</strong></Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedUser?.dateOfBirth
                    ? new Date(selectedUser.dateOfBirth).toLocaleDateString()
                    : "-"}
                </Typography>

                <Typography variant="subtitle2"><strong>Member Since:</strong></Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {formatDate(selectedUser?.createdAt)}
                </Typography>

                <Typography variant="subtitle2"><strong>About:</strong></Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedUser?.about || "-"}
                </Typography>
              </Paper>
            </Box>
          )}

          {userSubscriptionHistory.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Subscription History
              </Typography>
              {userSubscriptionHistory.map((s) => (
                <Box
                  key={s._id || s.purchaseId}
                  sx={{ mb: 2, pb: 1, borderBottom: "1px solid #eee" }}
                >
                  <Typography variant="subtitle2">
                    {s.planType ? s.planType.toUpperCase() : "Plan"} • {s.billingCycle || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Amount: {s.amount ?? "N/A"} {s.currency ?? ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Purchase: {s.purchaseDate ? moment(s.purchaseDate).format("DD MMM YYYY, hh:mm A") : "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expiry: {s.expiryDate ? moment(s.expiryDate).format("DD MMM YYYY, hh:mm A") : "N/A"}
                  </Typography>
                  <StatusChip status={s?.status} />
                </Box>
              ))}
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenDetailDialog(false)}>Close</Button>
          {selectedUser && !selectedUser.isBlocked && (
            <Button
              variant="contained"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => handleBlockUser(selectedUser._id)}
              disabled={actionInProgress}
            >
              Block User
            </Button>
          )}
          {selectedUser && selectedUser.isBlocked && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PersonAddIcon />}
              onClick={() => handleUnblockUser(selectedUser._id)}
              disabled={actionInProgress}
            >
              Unblock User
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommunityUsersManager;
